import {
  POWER_UP_CATEGORIES,
  POWER_UP_PRICING_MODELS,
  type PowerUpCategory,
  type PowerUpPricingModel,
} from '@molt/shared';
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import {
  PowerUpDirectory,
  type PowerUpDirectoryCategoryFilter,
  type PowerUpDirectoryPricingFilter,
} from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { usePowerUpDirectory } from '@/lib/power-ups';

interface PowerUpsSearch {
  q?: string;
  category?: PowerUpCategory;
  madeByUs?: boolean;
  pricingModel?: PowerUpPricingModel;
}

function stringValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

function categoryValue(value: unknown): PowerUpCategory | undefined {
  return typeof value === 'string' &&
    POWER_UP_CATEGORIES.includes(value as PowerUpCategory)
    ? (value as PowerUpCategory)
    : undefined;
}

function pricingValue(value: unknown): PowerUpPricingModel | undefined {
  return typeof value === 'string' &&
    POWER_UP_PRICING_MODELS.includes(value as PowerUpPricingModel)
    ? (value as PowerUpPricingModel)
    : undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

function powerUpsHref(search: PowerUpsSearch): string {
  const params = new URLSearchParams();
  if (search.q !== undefined) params.set('q', search.q);
  if (search.category !== undefined) params.set('category', search.category);
  if (search.madeByUs === true) params.set('madeByUs', 'true');
  if (search.pricingModel !== undefined) {
    params.set('pricingModel', search.pricingModel);
  }
  const query = params.toString();
  return query === '' ? '/power-ups' : `/power-ups?${query}`;
}

export const Route = createFileRoute('/power-ups')({
  validateSearch: (search: Record<string, unknown>): PowerUpsSearch => {
    const q = stringValue(search.q);
    const category = categoryValue(search.category);
    const madeByUs = booleanValue(search.madeByUs);
    const pricingModel = pricingValue(search.pricingModel);
    return {
      ...(q === undefined ? {} : { q }),
      ...(category === undefined ? {} : { category }),
      ...(madeByUs === true ? { madeByUs } : {}),
      ...(pricingModel === undefined ? {} : { pricingModel }),
    };
  },
  component: PowerUpsRoute,
});

function PowerUpsRoute(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const search = Route.useSearch();
  const { t } = useInterfaceLanguage();
  const [draftQuery, setDraftQuery] = useState(search.q ?? '');
  const directory = usePowerUpDirectory(search);

  useEffect(() => {
    setDraftQuery(search.q ?? '');
  }, [search.q]);

  const currentSearch = useMemo<PowerUpsSearch>(
    () => ({
      ...(search.q === undefined ? {} : { q: search.q }),
      ...(search.category === undefined ? {} : { category: search.category }),
      ...(search.madeByUs === true ? { madeByUs: true } : {}),
      ...(search.pricingModel === undefined ? {} : { pricingModel: search.pricingModel }),
    }),
    [search.category, search.madeByUs, search.pricingModel, search.q],
  );

  function update(next: PowerUpsSearch): void {
    void navigate({ href: powerUpsHref(next) });
  }

  function updateQuery(query: string): void {
    const next: PowerUpsSearch = { ...currentSearch };
    const trimmed = query.trim();
    if (trimmed === '') {
      delete next.q;
    } else {
      next.q = trimmed;
    }
    update(next);
  }

  function updateCategory(category: PowerUpDirectoryCategoryFilter): void {
    const next: PowerUpsSearch = { ...currentSearch };
    if (category === 'all') {
      delete next.category;
    } else {
      next.category = category;
    }
    update(next);
  }

  function updatePricingModel(pricingModel: PowerUpDirectoryPricingFilter): void {
    const next: PowerUpsSearch = { ...currentSearch };
    if (pricingModel === 'all') {
      delete next.pricingModel;
    } else {
      next.pricingModel = pricingModel;
    }
    update(next);
  }

  function updateMadeByUs(madeByUs: boolean): void {
    const next: PowerUpsSearch = { ...currentSearch };
    if (madeByUs) {
      next.madeByUs = true;
    } else {
      delete next.madeByUs;
    }
    update(next);
  }

  if (location.pathname !== '/power-ups') {
    return <Outlet />;
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold text-ink">
          {t('powerUps.directoryRoute.title')}
        </h1>
        <p className="max-w-3xl text-sm text-ink-muted">
          {t('powerUps.directoryRoute.description')}
        </p>
      </header>

      <PowerUpDirectory
        entries={directory.data?.powerUps ?? []}
        query={draftQuery}
        selectedCategory={search.category ?? 'all'}
        selectedPricingModel={search.pricingModel ?? 'all'}
        madeByUsOnly={search.madeByUs === true}
        loading={directory.isPending}
        error={directory.error?.message}
        boardContext={null}
        onQueryChange={setDraftQuery}
        onSearch={updateQuery}
        onSelectCategory={updateCategory}
        onSelectPricingModel={updatePricingModel}
        onMadeByUsOnlyChange={updateMadeByUs}
        onOpenDetail={(powerUp) =>
          void navigate({
            to: '/power-ups/$powerUpId',
            params: { powerUpId: powerUp.id },
          })
        }
        onRetry={() => void directory.refetch()}
      />
    </div>
  );
}
